import { useMemo } from 'react';
import { parseToHsl } from 'polished';
import { renderReactDesignTokens } from '../design-tokens/render/react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useLanguage } from '../locale/useTextLocalizer';
import { useGlobalSettingsThemeParameters } from './theme';

/**
 * Derive values for the `dt` object
 *
 * @param inverseTheme - specify whether or not to derive values for the inverse theme
 */
export function useDeriveDesignTokens(contextValue, inverted) {
  const globalSettingsOverrides = useGlobalSettingsThemeParameters();
  const language = useLanguage();

  // Feature flag for new token values
  const featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const dt = useMemo(() => {
    const customFocusColor = globalSettingsOverrides.customFocusColor;
    const customFocusOpacity = globalSettingsOverrides.customFocusOpacity;
    const customFocusThickness = globalSettingsOverrides.customFocusThickness;
    const theme = inverted ? contextValue.inverseTheme : contextValue.currentTheme;
    const dt = renderReactDesignTokens(theme, {
      lang: language
    }, featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues);
    function generateCustomFocusColor(tokenColor) {
      if (customFocusColor || customFocusOpacity) {
        const hsl = parseToHsl(customFocusColor ?? tokenColor);
        // since statefocusnova token does not support opacity, using hsla format for color
        return `hsla(${Math.round(hsl.hue)}, ${Math.round(hsl.saturation * 100)}%, ${Math.round(hsl.lightness * 100)}%, ${customFocusOpacity ? parseFloat(customFocusOpacity) / 100 : '1'})`;
      }
      return tokenColor;
    }
    return theme.enableNovaTokens ? {
      ...dt,
      tokensExperimental: {
        ...dt.tokensExperimental,
        ...theme.tokensExperimental,
        'color-action-background-focus': generateCustomFocusColor(theme.tokensExperimental?.['color-action-background-focus'] ?? dt.tokensExperimental['color-action-background-focus']),
        'color-action-background-focus-inverse': generateCustomFocusColor(theme.tokensExperimental?.['color-action-background-focus-inverse'] ?? dt.tokensExperimental['color-action-background-focus-inverse']),
        'color-action-border-focus-inverse': generateCustomFocusColor(theme.tokensExperimental?.['color-action-border-focus-inverse'] ?? dt.tokensExperimental['color-action-border-focus-inverse']),
        'size-action-border-width-focus': customFocusThickness ?? theme.tokensExperimental?.['size-action-border-width-focus'] ?? dt.tokensExperimental['size-action-border-width-focus'],
        'color-action-border-focus': generateCustomFocusColor(theme.tokensExperimental?.['color-action-border-focus'] ?? dt.tokensExperimental['color-action-border-focus'])
      },
      tokens: {
        ...dt.tokens,
        ...theme.tokens,
        'color-action-background-focus': generateCustomFocusColor(theme.tokens?.['color-action-background-focus'] ?? dt.tokens['color-action-background-focus']),
        'color-action-background-focus-inverse': generateCustomFocusColor(theme.tokens?.['color-action-background-focus-inverse'] ?? dt.tokens['color-action-background-focus-inverse']),
        'color-action-border-focus-inverse': generateCustomFocusColor(theme.tokens?.['color-action-border-focus-inverse'] ?? dt.tokens['color-action-border-focus-inverse']),
        'size-action-border-width-focus': customFocusThickness ?? theme.tokens?.['size-action-border-width-focus'] ?? dt.tokens['size-action-border-width-focus'],
        'color-action-border-focus': generateCustomFocusColor(theme.tokens?.['color-action-border-focus'] ?? dt.tokens['color-action-border-focus'])
      },
      stateFocusNova: {
        ...dt.stateFocusNova,
        onLightBg: {
          ...dt.stateFocusNova.onLightBg,
          bd: generateCustomFocusColor(dt.tokens['color-action-border-focus']),
          bdWidth: customFocusThickness ?? dt.tokens['size-border-width-l']
        },
        onDarkBg: {
          ...dt.stateFocusNova.onDarkBg,
          bd: generateCustomFocusColor(dt.tokens['color-action-border-focus-inverse']),
          bdWidth: customFocusThickness ?? dt.tokens['size-border-width-l']
        },
        onRightClick: {
          ...dt.stateFocusNova.onRightClick,
          bd: generateCustomFocusColor(dt.tokens['color-action-border-focus']),
          bdWidth: customFocusThickness ?? dt.tokens['size-border-width-s']
        }
      }
    } : {
      ...dt,
      tokensExperimental: {
        ...dt.tokensExperimental,
        ...theme.tokensExperimental
      },
      tokens: {
        ...dt.tokens,
        ...theme.tokens
      },
      stateFocus: {
        ...dt.stateFocus,
        onLightBg: {
          ...dt.reportTokens.stateFocus.onLightBg,
          bd: generateCustomFocusColor(dt.reportTokens.stateFocus.onLightBg.bd),
          bdWidth: customFocusThickness ?? dt.reportTokens.stateFocus.onLightBg.bdWidth
        },
        onDarkBg: {
          ...dt.reportTokens.stateFocus.onDarkBg,
          bd: generateCustomFocusColor(dt.reportTokens.stateFocus.onDarkBg.bd),
          bdWidth: customFocusThickness ?? dt.reportTokens.stateFocus.onDarkBg.bdWidth
        },
        onRightClick: {
          ...dt.reportTokens.stateFocus.onRightClick,
          bd: generateCustomFocusColor(dt.reportTokens.stateFocus.onRightClick.bd),
          bdWidth: customFocusThickness ?? dt.reportTokens.stateFocus.onRightClick.bdWidth
        }
      }
    };
  }, [globalSettingsOverrides.customFocusColor, globalSettingsOverrides.customFocusOpacity, globalSettingsOverrides.customFocusThickness, inverted, contextValue.inverseTheme, contextValue.currentTheme, language, featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues]);
  return dt;
}
//# sourceMappingURL=useDeriveDesignTokens.js.map